import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { getOptionsQueryParams } from "@app/class/Utilities";
import { IPlot } from "@app/interfaces/IPlot";
import { IVehicle } from "@app/interfaces/IVehicle";
import { IStreamaxExtendedDeviceInfo } from "@app/main/modules/foundation/_routes/hardware/_models/IStreamaxExtendedDeviceInfo";
import { FoundationPeripheralService } from "@app/main/modules/foundation/_services/peripheral.service.ajs";
import { PlotService } from "@app/services/plot.service";
import { ReasonCodeService } from "@app/services/reasonCode.service";
import { TranslationService } from "@app/services/translation.service";
import { forkJoin, from, lastValueFrom, Observable, Subject, timer } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { VideoStatus } from "../enums/VideoStatus";
import { CameraConfigUrlDto } from "../models/camera-config-url-dto";
import { ICameraChannel } from "../models/ICameraChannel";
import { IVideoEntry } from "../models/IVideoEntry";
import { RefreshOnlineStatusData } from "../models/refresh-online-status.data";
import { VideoEntryUtils } from "../utils/video-entry-utils";
import { CameraOnlineHistoryService } from "./camera-online-history.service";

@Injectable({
    providedIn: 'root'
})
export class CameraStatusService {
    private forceStatusUpdateSubject = new Subject<void>();
    forceStatusUpdate$ = this.forceStatusUpdateSubject.asObservable();

    constructor(
        private readonly http: HttpClient,
        private readonly plotService: PlotService,
        private readonly cameraOnlineHistoryService: CameraOnlineHistoryService,
        private readonly foundationPeripheralService: FoundationPeripheralService,
        private readonly translationService: TranslationService,
        private readonly reasonCodeService: ReasonCodeService
    ) { }

    getDeviceInfo(serial: string): Observable<IStreamaxExtendedDeviceInfo> {
        const result = this.http.get<IStreamaxExtendedDeviceInfo>(`/api/ftcloudapi/${serial}/deviceinfo`);
        return result;
    }

    getCameraConfigPageUrl(serial: string, supplierCode: string): Observable<CameraConfigUrlDto> {
        return this.http.get<CameraConfigUrlDto>(`/api/ftcloudapi/${serial}/configurl`, { params: { supplierCode } });
    }

    wakeUpStreamaxCamera(serial: string): Observable<void> {
        return this.http.post<void>(`/api/ftcloudapi/${serial}/wakeup`, null)
    }

    forceStatusUpdate(): void {
        this.forceStatusUpdateSubject.next();
    }

    createOnlineStatusObservable(vehicle: IVehicle, intervalMs: number = 60_000): Observable<RefreshOnlineStatusData> {
        return timer(0, intervalMs).pipe(
            switchMap(() => forkJoin([
                from(this.plotService.getLatestPlotForVehicle(vehicle.Id) as Promise<IPlot>),
                from(this.getVideoChannels(vehicle.Id)),
                from(this.cameraOnlineHistoryService.getCameraOnlineStatus(vehicle.Id)),
            ])),
            map(([plot, streamingChannels, onlineStatus]) => {
                return {
                    plot,
                    streamingChannels,
                    onlineStatus: this.foundationPeripheralService.getOnlineStatus(vehicle, onlineStatus)
                }
            }),
        )
    }

    async getVideoChannels(vehicleId: number): Promise<ICameraChannel[]> {
        const options = getOptionsQueryParams({ VehicleId: vehicleId });

        const result = await lastValueFrom(this.http
            .get<ICameraChannel[]>(`/api/cameras/channels`, options));

        return result;
    }

    getVideoDownloadStatusLabel(videoEntry: IVideoEntry): string {
        if (VideoEntryUtils.hasNoFootage(videoEntry)) {
            return this.translationService.t("VideoStatusEnum.NoFootageAvailable");
        }

        if (!videoEntry.vehicle?.onlineStatus?.online && VideoEntryUtils.isRequested(videoEntry)) {
            return this.translationService.t("cameraService.CameraOffline");
        }

        if (VideoEntryUtils.isRequested(videoEntry)) {
            return this.translationService.t("cameraService.Requested");
        }

        if (VideoEntryUtils.isDownloading(videoEntry)) {
            return this.translationService.t("cameraService.Downloading");
        }

        if (VideoEntryUtils.isDownloaded(videoEntry)) {
            return this.translationService.t("cameraService.Downloaded");
        }

        if (VideoEntryUtils.isFaceblurring(videoEntry)) {
            return this.translationService.t("cameraService.Processing");
        }

        return this.translationService.tEnum(VideoStatus, "VideoStatus", videoEntry.Status);
    }

    getVideoDisplayStatus(videoEntry: IVideoEntry): string {
        if (videoEntry.Status == VideoStatus.EventOnly &&
            videoEntry.MetaData.Authorised &&
            videoEntry.MetaData.Authorised.length !== 0) {
            return this.translationService.t("cameraService.noActionRequired");
        }

        const displayName = this.translationService.tEnum(VideoStatus, "VideoStatus", videoEntry.Status);

        return displayName || VideoStatus[videoEntry.Status];
    }

    getVideoRequestStatus(videoEvent: IVideoEntry): string {
        const statuses = [
            VideoStatus.Downloaded,
            VideoStatus.Error,
            VideoStatus.Cancelled,
            VideoStatus.NoFootageAvailable,
            VideoStatus.NoFilesAvailable,
            VideoStatus.Restricted,
            VideoStatus.Expired,
            VideoStatus.HardwareDeleted,
        ];

        if (statuses.includes(videoEvent.Status)) {
            return this.getVideoDisplayStatus(videoEvent);
        }

        const plot = videoEvent.vehicle.Plot;
        const isIgnitionOn = this.reasonCodeService.isIgnitionOn(plot.Reason);
        const channelsLength = videoEvent.vehicle.videoChannels?.length;

        if (plot && !isIgnitionOn || (channelsLength)) {
            return this.translationService.t("cameraService.Offline");
        }

        return this.getVideoDisplayStatus(videoEvent);
    }
}
